import { Col, Container, Row } from 'reactstrap';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useControllerDashboard from './Dashboard/_presenters/useControllerDashboard/useControllerDashboard';
import {
  StatCard,
  QuickActionsCard,
  RecentItemsCard,
  DeviceStatusCard
} from 'components/Dashboard';
import { ROUTES_PATHS } from 'navigations/routes';

const DashboardPage = () => {
  const navigate = useNavigate();
  const {
    stats,
    isLoading,
    recentPlaylists,
    recentCampaigns,
    recentDevices
  } = useControllerDashboard();

  const routes = ROUTES_PATHS.LOGGED_PAGES;

  const quickActions = useMemo(
    () => [
      {
        title: 'Nova Campanha',
        description: 'Criar uma nova campanha de mídia',
        icon: 'film',
        to: routes.CAMPAIGN.CREATE,
        iconColor: 'text-success'
      },
      {
        title: 'Nova Playlist',
        description: 'Criar uma nova playlist',
        icon: 'collection-play',
        to: routes.PLAYLIST.CREATE,
        iconColor: 'text-primary'
      },
      {
        title: 'Adicionar Mídia',
        description: 'Fazer upload de novas mídias',
        icon: 'cloud-upload',
        to: `${routes.CORPORATE_TV.LIST}?tab=medias`,
        iconColor: 'text-info'
      },
      {
        title: 'Gerenciar Dispositivos',
        description: 'Ver e configurar dispositivos',
        icon: 'laptop',
        to: routes.DEVICES.LIST,
        iconColor: 'text-warning'
      }
    ],
    [routes]
  );

  const recentPlaylistsFormatted = useMemo(
    () =>
      recentPlaylists.map((p) => ({
        id: p.id || '',
        name: p.name || 'Sem nome',
        status: p.active,
        extra: p.deviceGroup?.name
      })),
    [recentPlaylists]
  );

  const recentCampaignsFormatted = useMemo(
    () =>
      recentCampaigns.map((c) => ({
        id: c.id || '',
        name: c.name || 'Sem nome',
        status: c.active
      })),
    [recentCampaigns]
  );

  const recentDevicesFormatted = useMemo(
    () =>
      recentDevices.map((d) => ({
        id: d.id || '',
        name: d.name || 'Sem nome',
        status: d.active,
        extra: d.group?.name || d.local
      })),
    [recentDevices]
  );

  return (
    <>
      <div className="py-4">
        <Container fluid>
          {/* Header */}
          <Row className="mb-4">
            <Col>
              <h2 className="fw-bold mb-1">Dashboard</h2>
              <p className="text-muted mb-0">
                Visão geral do seu sistema de TV Corporativa
              </p>
            </Col>
          </Row>

          {/* Stats Cards */}
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Dispositivos"
                value={stats.devices.total}
                subtitle={`${stats.devices.online} online`}
                icon="laptop"
                iconBgColor="bg-primary"
                isLoading={isLoading}
                onClick={() => navigate(routes.DEVICES.LIST)}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Playlists"
                value={stats.playlists.total}
                subtitle={`${stats.playlists.active} ativas`}
                icon="collection-play"
                iconBgColor="bg-success"
                isLoading={isLoading}
                onClick={() =>
                  navigate(`${routes.CORPORATE_TV.LIST}?tab=playlists`)
                }
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Campanhas"
                value={stats.campaigns.total}
                subtitle={`${stats.campaigns.active} ativas`}
                icon="film"
                iconBgColor="bg-warning"
                isLoading={isLoading}
                onClick={() =>
                  navigate(`${routes.CORPORATE_TV.LIST}?tab=campaigns`)
                }
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <StatCard
                title="Mídias"
                value={stats.medias.total}
                subtitle={`${stats.deviceGroups.total} grupos`}
                icon="image"
                iconBgColor="bg-info"
                isLoading={isLoading}
                onClick={() =>
                  navigate(`${routes.CORPORATE_TV.LIST}?tab=medias`)
                }
              />
            </Col>
          </Row>

          {/* Main Content */}
          <Row className="g-4 mb-4">
            {/* Device Status */}
            <Col xs={12} lg={6}>
              <DeviceStatusCard
                total={stats.devices.total}
                online={stats.devices.online}
                active={stats.devices.active}
                inactive={stats.devices.inactive}
                isLoading={isLoading}
              />
            </Col>

            {/* Quick Actions */}
            <Col xs={12} lg={6}>
              <QuickActionsCard actions={quickActions} />
            </Col>
          </Row>

          {/* Recent Items */}
          <Row className="g-4">
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Playlists Recentes"
                icon="collection-play"
                items={recentPlaylistsFormatted}
                viewAllLink={`${routes.CORPORATE_TV.LIST}?tab=playlists`}
                isLoading={isLoading}
                emptyMessage="Nenhuma playlist criada"
              />
            </Col>
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Campanhas Recentes"
                icon="film"
                items={recentCampaignsFormatted}
                viewAllLink={`${routes.CORPORATE_TV.LIST}?tab=campaigns`}
                isLoading={isLoading}
                emptyMessage="Nenhuma campanha criada"
              />
            </Col>
            <Col xs={12} lg={4}>
              <RecentItemsCard
                title="Dispositivos Recentes"
                icon="laptop"
                items={recentDevicesFormatted}
                viewAllLink={routes.DEVICES.LIST}
                isLoading={isLoading}
                emptyMessage="Nenhum dispositivo cadastrado"
                statusLabels={{ active: 'Ativo', inactive: 'Inativo' }}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default DashboardPage;
